/**
 * Procedural sound.
 *
 * Every effect is synthesised from oscillators and noise rather than loaded
 * from files. That buys three things worth more than sample fidelity here:
 * nothing to download on a phone connection, no licensing question over sound
 * assets, and a "theme" becomes a handful of numbers, so four genuinely
 * different atmospheres cost almost nothing.
 *
 * Browsers will not start audio until the user has interacted, so the context
 * is created lazily on the first gesture and resumed defensively on every
 * play — iOS in particular suspends it whenever the tab loses focus.
 */

export type SoundName =
  | 'pickup' | 'drop' | 'invalid' | 'word' | 'declare' | 'deal'
  | 'peek' | 'good' | 'bad' | 'win' | 'tick' | 'join' | 'select';

export type ThemeId = 'wood' | 'arcade' | 'zen' | 'soft' | 'silly';

export interface Theme {
  id: ThemeId;
  name: string;
  description: string;
}

export const THEMES: Theme[] = [
  { id: 'wood', name: 'Wooden', description: 'Dry clacks and a low room. Like tiles on a kitchen table.' },
  { id: 'arcade', name: 'Arcade', description: 'Bright square-wave blips with a bit of swagger.' },
  { id: 'zen', name: 'Zen', description: 'Soft marimba and long tails. Calm, good for long games.' },
  { id: 'soft', name: 'Hushed', description: 'Barely-there taps. For playing next to someone asleep.' },
  { id: 'silly', name: 'Silly', description: 'Boings, squeaks, slide whistles, a kazoo fanfare and a sad trombone. For making a nine-year-old laugh.' },
];

export interface Voice {
  /** Oscillator shape; 'noise' routes a short noise burst instead. */
  type: OscillatorType | 'noise';
  freq: number;
  /** Frequency at the end of the sweep; equal to freq for no sweep. */
  to?: number;
  dur: number;
  gain: number;
  /** Low-pass cutoff; 0 leaves the signal unfiltered. */
  filter?: number;
  /** Seconds to wait before this voice sounds, for chords and arpeggios. */
  delay?: number;
  /**
   * Vibrato: the pitch wobbles `depth` Hz either side, `rate` times a second.
   * What turns a plain tone into a boing, a kazoo or a sad trombone.
   */
  wobble?: { rate: number; depth: number };
  /** Hold near full volume before fading, for sustained notes. 0 is percussive. */
  hold?: number;
}

type Recipe = Record<SoundName, Voice[]>;

const n = (freq: number, dur: number, gain: number, filter = 0, delay = 0): Voice =>
  ({ type: 'noise', freq, dur, gain, filter, delay });
const t = (
  type: OscillatorType, freq: number, to: number, dur: number, gain: number, delay = 0,
): Voice => ({ type, freq, to, dur, gain, delay });

/** A tone with vibrato, and optionally held before it fades. */
const w = (
  type: OscillatorType, freq: number, to: number, dur: number, gain: number,
  rate: number, depth: number, delay = 0, hold = 0,
): Voice => ({ type, freq, to, dur, gain, delay, wobble: { rate, depth }, hold });

/**
 * The silly set. Every sound is a cartoon cliché on purpose, because the
 * cliché is the joke: a spring for a tile landing, a squeak for picking one
 * up, a slide whistle for a word, a kazoo fanfare for the call, and — for a
 * call made too early — the sad trombone. Nothing is harsh or loud, because
 * the person laughing is probably sitting next to somebody trying to think.
 */
const SILLY: Recipe = {
  pickup: [t('sine', 380, 900, 0.07, 0.12)],                           // bloop
  drop: [w('sine', 170, 520, 0.26, 0.16, 22, 70)],                     // boing
  select: [w('sine', 1500, 2100, 0.06, 0.07, 40, 120)],                // squeak
  invalid: [w('sawtooth', 120, 85, 0.36, 0.10, 28, 18), n(180, 0.3, 0.05, 500)], // raspberry
  word: [t('sine', 420, 1400, 0.34, 0.11)],                            // slide whistle up
  declare: [                                                           // kazoo fanfare
    w('sawtooth', 392, 392, 0.16, 0.07, 7, 9, 0, 0.1),
    w('sawtooth', 523, 523, 0.16, 0.07, 7, 9, 0.17, 0.1),
    w('sawtooth', 659, 659, 0.16, 0.07, 7, 9, 0.34, 0.1),
    w('sawtooth', 784, 784, 0.42, 0.08, 7, 12, 0.51, 0.3),
  ],
  deal: [t('sine', 900, 220, 0.05, 0.10)],                             // pop
  peek: [t('sine', 784, 784, 0.14, 0.10), t('sine', 622, 622, 0.2, 0.10, 0.16)], // cuckoo
  good: [t('sine', 520, 1650, 0.3, 0.11)],                             // wheee
  bad: [                                                               // wah, wah, wah, wahhh
    w('sawtooth', 392, 377, 0.32, 0.08, 5, 6, 0, 0.2),
    w('sawtooth', 370, 356, 0.32, 0.08, 5, 6, 0.36, 0.2),
    w('sawtooth', 349, 336, 0.32, 0.08, 5, 6, 0.72, 0.2),
    w('sawtooth', 330, 300, 1.0, 0.09, 6, 14, 1.08, 0.6),
  ],
  win: [                                                               // fanfare, then whee
    w('sawtooth', 523, 523, 0.14, 0.07, 7, 9, 0, 0.08),
    w('sawtooth', 659, 659, 0.14, 0.07, 7, 9, 0.15, 0.08),
    w('sawtooth', 784, 784, 0.14, 0.07, 7, 9, 0.30, 0.08),
    w('sawtooth', 1047, 1047, 0.4, 0.08, 7, 12, 0.45, 0.25),
    t('sine', 600, 2000, 0.45, 0.09, 0.9),
  ],
  tick: [t('sine', 980, 760, 0.04, 0.05)],                             // tock
  join: [t('sine', 523, 523, 0.1, 0.09), t('sine', 784, 784, 0.16, 0.09, 0.12)], // hello
};

const RECIPES: Record<ThemeId, Recipe> = {
  silly: SILLY,
  wood: {
    pickup: [n(900, 0.05, 0.16, 2600)],
    drop: [n(520, 0.075, 0.30, 1700), t('sine', 190, 120, 0.07, 0.16)],
    select: [n(1400, 0.03, 0.10, 4000)],
    invalid: [t('sawtooth', 150, 90, 0.19, 0.13)],
    word: [t('triangle', 520, 780, 0.15, 0.13), t('triangle', 780, 1040, 0.13, 0.09, 0.07)],
    declare: [
      t('triangle', 392, 523, 0.17, 0.16), t('triangle', 523, 659, 0.17, 0.15, 0.09),
      t('triangle', 659, 784, 0.30, 0.16, 0.18), n(300, 0.4, 0.09, 900, 0.18),
    ],
    deal: [n(760, 0.055, 0.14, 2400)],
    peek: [t('sine', 880, 1180, 0.12, 0.11)],
    good: [t('sine', 660, 990, 0.20, 0.15)],
    bad: [t('sawtooth', 220, 110, 0.26, 0.14)],
    win: [
      t('triangle', 523, 523, 0.19, 0.16), t('triangle', 659, 659, 0.19, 0.16, 0.13),
      t('triangle', 784, 784, 0.19, 0.16, 0.26), t('triangle', 1047, 1047, 0.58, 0.19, 0.39),
    ],
    tick: [n(1500, 0.02, 0.07, 5000)],
    join: [t('sine', 440, 660, 0.13, 0.11)],
  },
  arcade: {
    pickup: [t('square', 660, 880, 0.045, 0.09)],
    drop: [t('square', 440, 330, 0.055, 0.11)],
    select: [t('square', 990, 1320, 0.035, 0.07)],
    invalid: [t('square', 200, 110, 0.18, 0.12)],
    word: [t('square', 660, 990, 0.08, 0.10), t('square', 990, 1320, 0.09, 0.09, 0.05)],
    declare: [
      t('square', 523, 523, 0.07, 0.12), t('square', 784, 784, 0.07, 0.12, 0.06),
      t('square', 1047, 1047, 0.19, 0.13, 0.12),
    ],
    deal: [t('square', 1200, 900, 0.04, 0.07)],
    peek: [t('square', 1320, 1760, 0.07, 0.09)],
    good: [t('square', 880, 1320, 0.14, 0.11)],
    bad: [t('sawtooth', 160, 70, 0.28, 0.13)],
    win: [
      t('square', 523, 523, 0.09, 0.12), t('square', 659, 659, 0.09, 0.12, 0.09),
      t('square', 784, 784, 0.09, 0.12, 0.18), t('square', 1047, 1047, 0.45, 0.15, 0.27),
    ],
    tick: [t('square', 1800, 1800, 0.015, 0.05)],
    join: [t('square', 523, 784, 0.10, 0.09)],
  },
  zen: {
    pickup: [t('sine', 523, 523, 0.24, 0.07)],
    drop: [t('sine', 392, 392, 0.34, 0.11), t('sine', 784, 784, 0.30, 0.04)],
    select: [t('sine', 880, 880, 0.18, 0.05)],
    invalid: [t('sine', 233, 220, 0.44, 0.09)],
    word: [t('sine', 523, 523, 0.5, 0.09), t('sine', 659, 659, 0.5, 0.07, 0.08),
           t('sine', 784, 784, 0.6, 0.06, 0.16)],
    declare: [
      t('sine', 440, 440, 0.7, 0.11), t('sine', 554, 554, 0.7, 0.09, 0.12),
      t('sine', 659, 659, 0.9, 0.09, 0.24), t('sine', 880, 880, 1.1, 0.07, 0.36),
    ],
    deal: [t('sine', 698, 698, 0.22, 0.06)],
    peek: [t('sine', 1047, 1047, 0.34, 0.06)],
    good: [t('sine', 659, 988, 0.5, 0.09)],
    bad: [t('sine', 196, 165, 0.6, 0.10)],
    win: [
      t('sine', 523, 523, 0.9, 0.10), t('sine', 659, 659, 0.9, 0.09, 0.18),
      t('sine', 784, 784, 1.0, 0.09, 0.36), t('sine', 1047, 1047, 1.5, 0.09, 0.54),
    ],
    tick: [t('sine', 1319, 1319, 0.1, 0.03)],
    join: [t('sine', 587, 880, 0.4, 0.07)],
  },
  soft: {
    pickup: [n(1100, 0.022, 0.045, 1800)],
    drop: [n(600, 0.035, 0.07, 1100)],
    select: [n(1500, 0.015, 0.035, 2600)],
    invalid: [t('sine', 180, 140, 0.13, 0.05)],
    word: [t('sine', 620, 760, 0.1, 0.05)],
    declare: [t('sine', 523, 784, 0.26, 0.07), t('sine', 784, 1047, 0.22, 0.05, 0.1)],
    deal: [n(900, 0.025, 0.045, 1800)],
    peek: [t('sine', 980, 1180, 0.09, 0.045)],
    good: [t('sine', 700, 950, 0.16, 0.06)],
    bad: [t('sine', 200, 150, 0.2, 0.06)],
    win: [t('sine', 659, 659, 0.2, 0.07), t('sine', 988, 988, 0.5, 0.07, 0.16)],
    tick: [n(1600, 0.012, 0.025, 4000)],
    join: [t('sine', 520, 700, 0.14, 0.05)],
  },
};

export interface AudioSettings { muted: boolean; theme: ThemeId; volume: number; }

export class AudioEngine {
  #ctx: AudioContext | null = null;
  #master: GainNode | null = null;
  #noise: AudioBuffer | null = null;
  #settings: AudioSettings = { muted: false, theme: 'wood', volume: 0.7 };
  /** Rate-limit: a fast drag would otherwise fire dozens of clacks a second. */
  #lastAt = new Map<SoundName, number>();

  get settings(): AudioSettings { return { ...this.#settings }; }

  /**
   * State of the underlying audio context, for diagnostics.
   *
   * `'none'` means no context has been created yet; browsers refuse to make
   * one before a user gesture. Anything other than `'running'` means nothing
   * will be heard, which is otherwise invisible on a device where the tester
   * cannot listen.
   */
  get contextState(): 'none' | AudioContextState {
    return this.#ctx?.state ?? 'none';
  }

  configure(patch: Partial<AudioSettings>): void {
    this.#settings = { ...this.#settings, ...patch };
    if (this.#master && this.#ctx) {
      this.#master.gain.setTargetAtTime(
        this.#settings.muted ? 0 : this.#settings.volume, this.#ctx.currentTime, 0.02);
    }
  }

  /** Call from a real user gesture; safe to call repeatedly. */
  unlock(): void {
    if (this.#settings.muted) return;
    this.#ensure();
    void this.#ctx?.resume();
  }

  #ensure(): void {
    if (this.#ctx) return;
    const Ctor = window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;                       // no Web Audio: the game is silent, not broken
    const ctx = new Ctor();
    const master = ctx.createGain();
    master.gain.value = this.#settings.muted ? 0 : this.#settings.volume;
    master.connect(ctx.destination);
    this.#ctx = ctx;
    this.#master = master;

    const len = Math.floor(ctx.sampleRate * 0.4);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.#noise = buf;
  }

  play(name: SoundName, opts: { throttleMs?: number; detune?: number } = {}): void {
    if (this.#settings.muted) return;
    const now = performance.now();
    const gap = opts.throttleMs ?? 26;
    if (now - (this.#lastAt.get(name) ?? -1e9) < gap) return;
    this.#lastAt.set(name, now);

    this.#ensure();
    const ctx = this.#ctx;
    const master = this.#master;
    if (!ctx || !master) return;
    if (ctx.state === 'suspended') void ctx.resume();

    const recipe = RECIPES[this.#settings.theme][name];
    const bend = opts.detune ? Math.pow(2, opts.detune / 1200) : 1;
    for (const v of recipe) this.#voice(ctx, master, v, bend);
  }

  #voice(ctx: AudioContext, out: GainNode, v: Voice, bend: number): void {
    const t0 = ctx.currentTime + (v.delay ?? 0);
    const gain = ctx.createGain();
    // Short attack then exponential decay: percussive without a click.
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, v.gain), t0 + 0.006);
    // A held note stays up before it fades, the way a kazoo or trombone does.
    if (v.hold && v.hold > 0) gain.gain.setValueAtTime(Math.max(0.0002, v.gain), t0 + v.hold);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + v.dur);

    let node: AudioNode;
    if (v.type === 'noise' && this.#noise) {
      const src = ctx.createBufferSource();
      src.buffer = this.#noise;
      src.loop = true;          // the buffer is short; a long crash needs more of it
      src.start(t0);
      src.stop(t0 + v.dur + 0.02);
      node = src;
    } else {
      const osc = ctx.createOscillator();
      osc.type = v.type === 'noise' ? 'triangle' : v.type;
      osc.frequency.setValueAtTime(v.freq * bend, t0);
      if (v.to && v.to !== v.freq) {
        osc.frequency.exponentialRampToValueAtTime(Math.max(20, v.to * bend), t0 + v.dur);
      }
      if (v.wobble) {
        const lfo = ctx.createOscillator();
        const depth = ctx.createGain();
        lfo.frequency.value = v.wobble.rate;
        depth.gain.value = v.wobble.depth * bend;
        lfo.connect(depth).connect(osc.frequency);
        lfo.start(t0);
        lfo.stop(t0 + v.dur + 0.02);
      }
      osc.start(t0);
      osc.stop(t0 + v.dur + 0.02);
      node = osc;
    }

    if (v.filter && v.filter > 0) {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = v.filter;
      node.connect(lp).connect(gain).connect(out);
    } else {
      node.connect(gain).connect(out);
    }
  }

  /**
   * Play a sound described on the spot rather than one of the theme's.
   * For the start countdown, whose styles are chosen separately from the
   * sound theme.
   */
  playVoices(voices: readonly Voice[]): void {
    if (this.#settings.muted) return;
    this.#ensure();
    const ctx = this.#ctx;
    const master = this.#master;
    if (!ctx || !master) return;
    if (ctx.state === 'suspended') void ctx.resume();
    for (const v of voices) this.#voice(ctx, master, v, 1);
  }

  /** Short haptic where the platform offers one; silently ignored elsewhere. */
  buzz(ms = 12): void {
    if (this.#settings.muted) return;
    try { navigator.vibrate?.(ms); } catch { /* unsupported */ }
  }
}

export const audio = new AudioEngine();
